import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Controller, useForm } from 'react-hook-form';
import { styles } from './styles';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    TextField,
    withStyles,
    makeStyles,
    IconButton,
    Typography
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { modalsButtonStyles } from '../../../collums-constants/styles/stylesheets/buttonsStyles';
import Button from '@material-ui/core/Button';
import PhoneNumberInput from 'material-ui-phone-number';
import { PREFERRED_PHONE_COUNTRIES } from '../../../collums-constants';
import { getOrganisationCountryCode } from '../../../collums-components/helpers';
import { useDispatch, useSelector } from 'react-redux';
import InsuranceApi from '../../../api/insuranceApi';
import { updateInsuranceSuccess } from '../../../actions/customerActions';

const InsuranceDetailsModal = ({ open, onClose, classes, customer, insurance = {}, afterSave }) => {
    const globalStyles = makeStyles(modalsButtonStyles)();
    const organisation = useSelector(state => state.calendar.organisation);
    const dispatch = useDispatch();

    const { handleSubmit, errors, control, reset } = useForm({
        defaultValues: {
            company: '',
            number: '',
            authNumber: '',
            phone: '',
            other: '',
            ...insurance
        }
    });

    useEffect(() => {
        reset(insurance);
    }, [insurance, reset]);

    const saveHandler = useCallback(
        async value => {
            if (Object.keys(errors).length) return;

            try {
                onClose();
                const responseData = await InsuranceApi.update(customer.id, value);
                dispatch(updateInsuranceSuccess(responseData));
            } finally {
                if (afterSave) {
                    afterSave();
                }
            }
        },
        [afterSave, customer.id, dispatch, errors, onClose]
    );

    const getCountryCode = () => {
        return getOrganisationCountryCode(organisation);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle className={classes.formDialogTitle}>
                <Typography className={`${classes.bold} ${classes.formDialogTitleLabel}`}>Insurance Details</Typography>
                <IconButton aria-label="close" onClick={onClose} className={classes.formDialogCloseButton}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.formDialogContent}>
                <form
                    onSubmit={e => {
                        e.preventDefault();
                        handleSubmit(saveHandler)();
                    }}
                >
                    <Grid container spacing={2} direction="column">
                        <Grid item xs={12}>
                            <FormControl fullWidth={true}>
                                <Controller
                                    name={'company'}
                                    control={control}
                                    render={({ value, onChange }) => (
                                        <TextField
                                            name="company"
                                            variant="outlined"
                                            label="Insurance Company"
                                            onChange={onChange}
                                            value={value}
                                        />
                                    )}
                                ></Controller>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth={true}>
                                <Controller
                                    name={'number'}
                                    control={control}
                                    render={({ value, onChange }) => (
                                        <TextField
                                            name="number"
                                            variant="outlined"
                                            label="Membership No."
                                            onChange={onChange}
                                            value={value}
                                        />
                                    )}
                                ></Controller>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth={true}>
                                <Controller
                                    name={'authNumber'}
                                    control={control}
                                    render={({ value, onChange }) => (
                                        <TextField
                                            name="authNumber"
                                            variant="outlined"
                                            label="Authorisation No."
                                            onChange={onChange}
                                            value={value}
                                        />
                                    )}
                                ></Controller>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth={true}>
                                <Controller
                                    name={'phone'}
                                    control={control}
                                    render={({ value, onChange }) => (
                                        <PhoneNumberInput
                                            required
                                            variant="outlined"
                                            value={value}
                                            preferredCountries={PREFERRED_PHONE_COUNTRIES}
                                            defaultCountry={getCountryCode()}
                                            onChange={onChange}
                                        />
                                    )}
                                ></Controller>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth={true}>
                                <Controller
                                    name={'other'}
                                    control={control}
                                    render={({ value, onChange }) => (
                                        <TextField
                                            name="other"
                                            variant="outlined"
                                            label="Other"
                                            onChange={onChange}
                                            value={value}
                                        />
                                    )}
                                ></Controller>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <div className={`${globalStyles.buttonsContainer}`}>
                        <Button className={`${globalStyles.cancelButton} ${classes.cancelButton}`} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button className={`${globalStyles.confirmButton} ${classes.confirmButton}`} type="submit">
                            Save
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

InsuranceDetailsModal.propTypes = {
    open: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    afterSave: PropTypes.func,
    customer: PropTypes.object.isRequired,
    classes: PropTypes.array,
    insurance: PropTypes.object
};

export default withStyles(styles)(InsuranceDetailsModal);
